import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  Boxes,
  CalendarDays,
  Download,
  Edit,
  File,
  FileText,
  Link2,
  Loader2,
  Paperclip,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import RoleGuard from '@/components/auth/RoleGuard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { trpc } from '@/lib/trpc';
import type { RouterOutputs } from '@/lib/trpc';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  type ShipmentStatus,
  shipmentStatusClassName,
} from '@/features/shipments/utils/status-style';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { PageActionBar, PageActionBarDivider } from '@/components/layout/PageActionBar';
import { BackToTopPill } from '@/components/BackToTopPill';
import { CommentTimeline } from '@/features/comments/components/CommentTimeline';
import { PageLoader } from '@/components/loading/PageLoader';

type Forwarder = RouterOutputs['shipments']['listForwarders'][number];
type PurchaseOrder = RouterOutputs['purchaseOrders']['getOrders']['data'][number];

// Widened document set. shipment_files.file_type gained 'arrival_notice' +
// 'packing_list' in migration 20260706060000. The backend/shared PR that
// widens the tRPC input enum is landing in parallel — the casts below are the
// documented seam until @threadheads/shared catches up.
type ShipmentFileType = 'invoice' | 'arrival_notice' | 'packing_list' | 'other';

// Cast-gate types — the new shipment logistics columns (migration
// 20260706060000) + the shipments.listShipmentCosts / setShipmentCosts /
// notifyWarehouse procedures land with the parallel backend PR. Types lag at
// typecheck; the runtime procedures exist once merged.
type TransportMode = 'sea_freight' | 'air_freight' | 'courier';
type ShipmentDetailExtras = {
  into_port_date: string | null;
  at_warehouse_date: string | null;
  mode_of_transport: TransportMode | null;
  total_units: number | null;
  total_cartons: number | null;
};

type ShipmentCostType = 'duty' | 'freight' | 'other';
type ShipmentCostRow = {
  id: string;
  cost_type: ShipmentCostType;
  amount: number;
  currency: string;
  notes: string | null;
};
type ShipmentCostInput = {
  id?: string;
  costType: ShipmentCostType;
  amount: number;
  currency: string;
  notes: string | null;
};
type NotifyWarehouseResult = { channel: string | null };

type QueryLike<T> = {
  data: T | undefined;
  isLoading: boolean;
  error: { message: string } | null;
  refetch: () => Promise<unknown>;
};
type MutationLike<I> = {
  mutateAsync: (input: I) => Promise<unknown>;
  mutate: (input: I) => void;
  isPending: boolean;
};
type ShipmentsExtApi = {
  listShipmentCosts: {
    useQuery: (
      input: { shipmentId: string },
      opts?: { enabled?: boolean; staleTime?: number },
    ) => QueryLike<ShipmentCostRow[]>;
  };
  setShipmentCosts: {
    useMutation: (opts?: {
      onSuccess?: () => void | Promise<void>;
      onError?: (err: { message: string }) => void;
    }) => MutationLike<{ shipmentId: string; costs: ShipmentCostInput[] }>;
  };
  notifyWarehouse: {
    useMutation: (opts?: {
      onSuccess?: (data: NotifyWarehouseResult) => void;
      onError?: (err: { message: string }) => void;
    }) => { mutate: (input: { shipmentId: string }) => void; isPending: boolean };
  };
};

const STATUS_OPTIONS: Array<{ value: ShipmentStatus; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const TRANSPORT_OPTIONS: Array<{ value: TransportMode; label: string }> = [
  { value: 'sea_freight', label: 'Sea freight' },
  { value: 'air_freight', label: 'Air freight' },
  { value: 'courier', label: 'Courier' },
];

const COST_TYPE_OPTIONS: Array<{ value: ShipmentCostType; label: string }> = [
  { value: 'duty', label: 'Duty' },
  { value: 'freight', label: 'Freight' },
  { value: 'other', label: 'Other' },
];

const CURRENCY_OPTIONS = ['AUD', 'USD', 'CNY', 'EUR', 'GBP'];

const transportLabel = (mode: string | null | undefined) =>
  TRANSPORT_OPTIONS.find((opt) => opt.value === mode)?.label ?? null;

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const normalizeCurrency = (value: string) => value.trim().toUpperCase();

const formatBytes = (bytes: number | null) => {
  if (bytes === null || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export default function ShipmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  // `allLocations`, not `locations`: the lookup below RESOLVES A NAME for a
  // stored shipment's destination, which may since have been delegated.
  const { allLocations } = useLocation();
  const { organization } = useOrganization();
  const { format: formatMoney } = useCurrencyFormatter();

  const shipmentId = id ?? '';
  const orgCurrency = normalizeCurrency(organization?.default_currency ?? 'USD');
  const locationLookup = useMemo(
    () => new Map(allLocations.map((l) => [l.id, l.name])),
    [allLocations],
  );

  const shipmentQuery = trpc.shipments.getShipment.useQuery(
    { shipmentId },
    { enabled: Boolean(shipmentId), staleTime: 30_000 }
  );

  const xeroStatusQuery = trpc.xero.getStatus.useQuery(undefined, {
    staleTime: 30_000,
  });

  const xeroAccountsQuery = trpc.xero.getAccounts.useQuery(
    { dropdownOnly: true },
    {
      staleTime: 30_000,
      enabled: xeroStatusQuery.data?.connected === true,
    }
  );

  const xeroTaxRatesQuery = trpc.xero.getTaxRates.useQuery(
    { activeOnly: true },
    {
      staleTime: 30_000,
      enabled: xeroStatusQuery.data?.connected === true,
    }
  );

  const forwardersQuery = trpc.shipments.listForwarders.useQuery(
    { includeInactive: true },
    { staleTime: 60_000 }
  );

  const updateShipment = trpc.shipments.updateShipment.useMutation({
    onSuccess: async () => {
      await utils.shipments.getShipment.invalidate({ shipmentId });
      await utils.shipments.listShipments.invalidate();
      toast({ title: 'Shipment updated' });
      setIsEditing(false);
    },
    onError: (err) => toast({ title: 'Failed to update shipment', description: err.message, variant: 'destructive' }),
  });

  const updateStatus = trpc.shipments.updateShipment.useMutation({
    onSuccess: async () => {
      await utils.shipments.getShipment.invalidate({ shipmentId });
      await utils.shipments.listShipments.invalidate();
      toast({ title: 'Status updated' });
    },
    onError: (err) => toast({ title: 'Failed to update status', description: err.message, variant: 'destructive' }),
  });

  const handleStatusChange = (newStatus: ShipmentStatus) => {
    if (!shipmentId || newStatus === shipment?.status) return;
    updateStatus.mutate({ shipmentId, status: newStatus });
  };

  const deleteShipment = trpc.shipments.deleteShipment.useMutation({
    onSuccess: async () => {
      await utils.shipments.listShipments.invalidate();
      toast({ title: 'Shipment deleted' });
      navigate('/shipments');
    },
    onError: (err) => toast({ title: 'Failed to delete shipment', description: err.message, variant: 'destructive' }),
  });

  const pushShipmentToXero = trpc.xero.pushShipment.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.shipments.getShipment.invalidate({ shipmentId }),
        utils.shipments.listShipments.invalidate(),
        utils.xero.getSyncLogs.invalidate(),
      ]);
      toast({ title: 'Shipment pushed to Xero' });
    },
    onError: (err) => toast({ title: 'Failed to push shipment to Xero', description: err.message, variant: 'destructive' }),
  });

  // Notify warehouse (cast-gated — procedure lands with the parallel backend PR).
  const shipmentsExt = trpc.shipments as unknown as ShipmentsExtApi;
  const notifyWarehouse = shipmentsExt.notifyWarehouse.useMutation({
    onSuccess: (data) => {
      const channel = data?.channel ? data.channel.replace(/^#?/, '#') : null;
      toast({ title: channel ? `Posted to ${channel}` : 'Warehouse notified' });
      setNotifyOpen(false);
    },
    onError: (err) => {
      const noChannel = /channel/i.test(err.message);
      toast({
        title: 'Could not notify warehouse',
        description: noChannel
          ? 'No warehouse Slack channel is configured. Set one under Notifications.'
          : err.message,
        variant: 'destructive',
      });
      setNotifyOpen(false);
    },
  });

  const linkPo = trpc.shipments.linkPurchaseOrder.useMutation({
    onSuccess: async () => {
      await utils.shipments.getShipment.invalidate({ shipmentId });
      toast({ title: 'Purchase order linked' });
      setPoPickerOpen(false);
    },
    onError: (err) => toast({ title: 'Failed to link PO', description: err.message, variant: 'destructive' }),
  });

  const unlinkPo = trpc.shipments.unlinkPurchaseOrder.useMutation({
    onSuccess: async () => {
      await utils.shipments.getShipment.invalidate({ shipmentId });
      toast({ title: 'Purchase order unlinked' });
    },
    onError: (err) => toast({ title: 'Failed to unlink PO', description: err.message, variant: 'destructive' }),
  });

  const shipmentFilesQuery = trpc.shipments.listShipmentFiles.useQuery(
    { shipmentId },
    { enabled: Boolean(shipmentId), staleTime: 30_000 }
  );

  const uploadShipmentFile = trpc.shipments.uploadShipmentFile.useMutation({
    onSuccess: async () => {
      await shipmentFilesQuery.refetch();
      toast({ title: 'File uploaded' });
    },
    onError: (err) => toast({ title: 'Failed to upload file', description: err.message, variant: 'destructive' }),
  });

  const deleteShipmentFileMutation = trpc.shipments.deleteShipmentFile.useMutation({
    onSuccess: async () => {
      await shipmentFilesQuery.refetch();
      toast({ title: 'File deleted' });
    },
    onError: (err) => toast({ title: 'Failed to delete file', description: err.message, variant: 'destructive' }),
  });

  const getShipmentFileDownloadUrl = trpc.shipments.getShipmentFileDownloadUrl.useMutation();

  const [uploadingFileType, setUploadingFileType] = useState<ShipmentFileType | null>(null);

  const shipmentFiles = useMemo(() => shipmentFilesQuery.data ?? [], [shipmentFilesQuery.data]);
  const filesByType = useMemo(() => {
    const byType = (type: ShipmentFileType) =>
      shipmentFiles.filter((f) => f.file_type === type);
    return {
      arrival_notice: byType('arrival_notice'),
      packing_list: byType('packing_list'),
      invoice: byType('invoice'),
    };
  }, [shipmentFiles]);
  const hasInvoice = filesByType.invoice.length > 0;

  const handleUploadShipmentFile = async (fileType: ShipmentFileType, file: globalThis.File) => {
    setUploadingFileType(fileType);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
      await uploadShipmentFile.mutateAsync({
        shipmentId,
        fileType,
        fileName: file.name,
        contentType: file.type || null,
        base64,
      } as Parameters<typeof uploadShipmentFile.mutateAsync>[0]);
    } finally {
      setUploadingFileType(null);
    }
  };

  const handleDownloadShipmentFile = async (fileId: string) => {
    try {
      const result = await getShipmentFileDownloadUrl.mutateAsync({ fileId });
      window.open(result.url, '_blank');
    } catch {
      toast({ title: 'Failed to get download link', variant: 'destructive' });
    }
  };

  const handleDeleteShipmentFile = (fileId: string, fileName: string) => {
    if (!confirm(`Delete "${fileName}"?`)) return;
    deleteShipmentFileMutation.mutate({ fileId });
  };

  const shipment = shipmentQuery.data;
  const shipmentExtras = shipment as (typeof shipment & Partial<ShipmentDetailExtras>) | undefined;
  const shipmentLocationId = shipment?.location_id;

  const purchaseOrdersQuery = trpc.purchaseOrders.getOrders.useQuery(
    { locationId: shipmentLocationId || undefined },
    { enabled: Boolean(shipmentLocationId), staleTime: 30_000 }
  );
  const forwarders = useMemo<Forwarder[]>(() => forwardersQuery.data ?? [], [forwardersQuery.data]);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [poPickerOpen, setPoPickerOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);

  const [form, setForm] = useState({
    freightForwarderId: '' as string,
    shippedDate: '' as string,
    expectedDeliveryDate: '' as string,
    intoPortDate: '' as string,
    atWarehouseDate: '' as string,
    modeOfTransport: '' as 'sea_freight' | 'air_freight' | 'courier' | '',
    totalUnits: '' as string,
    totalCartons: '' as string,
    invoiceNumber: '' as string,
    invoiceDate: '' as string,
    weight: '' as string,
    cbm: '' as string,
    trackingLink: '' as string,
    freightCost: '' as string,
    currency: orgCurrency,
    exchangeRate: '1' as string,
    taxRateId: '' as string,
    accountCode: '' as string,
    notes: '' as string,
  });

  useEffect(() => {
    if (!shipment) return;
    const extras = shipment as typeof shipment & Partial<ShipmentDetailExtras>;
    setForm({
      freightForwarderId: shipment.freight_forwarder_id ?? '',
      shippedDate: shipment.shipped_date ?? '',
      expectedDeliveryDate: shipment.expected_delivery_date ?? '',
      intoPortDate: extras.into_port_date ?? '',
      atWarehouseDate: extras.at_warehouse_date ?? '',
      modeOfTransport: extras.mode_of_transport ?? '',
      totalUnits: extras.total_units != null ? String(extras.total_units) : '',
      totalCartons: extras.total_cartons != null ? String(extras.total_cartons) : '',
      invoiceNumber: shipment.invoice_number ?? '',
      invoiceDate: shipment.invoice_date ?? '',
      weight: shipment.weight?.toString() ?? '',
      cbm: shipment.cbm?.toString() ?? '',
      trackingLink: shipment.tracking_link ?? '',
      freightCost: shipment.freight_cost?.toString() ?? '',
      currency: normalizeCurrency(shipment.currency ?? orgCurrency),
      exchangeRate: (shipment.exchange_rate ?? 1).toString(),
      taxRateId: shipment.tax_rate_id ?? '',
      accountCode: shipment.account_code ?? '',
      notes: shipment.notes ?? '',
    });
  }, [shipment, orgCurrency]);

  useEffect(() => {
    if (!form.freightForwarderId) return;
    const selectedForwarder = forwarders.find((f) => f.id === form.freightForwarderId);
    if (!selectedForwarder) return;

    setForm((prev) => {
      const nextCurrency =
        prev.currency || normalizeCurrency(selectedForwarder.default_currency ?? orgCurrency);
      const nextTaxRateId = prev.taxRateId || selectedForwarder.default_tax_rate_id || '';
      const nextAccountCode = prev.accountCode || selectedForwarder.default_account_code || '';

      if (
        nextCurrency === prev.currency &&
        nextTaxRateId === prev.taxRateId &&
        nextAccountCode === prev.accountCode
      ) {
        return prev;
      }

      return {
        ...prev,
        currency: nextCurrency,
        taxRateId: nextTaxRateId,
        accountCode: nextAccountCode,
      };
    });
  }, [form.freightForwarderId, forwarders, orgCurrency]);

  const linkedPurchaseOrders = useMemo(
    () => shipment?.purchase_orders ?? [],
    [shipment?.purchase_orders]
  );
  const linkedPoIds = useMemo(() => new Set<string>(linkedPurchaseOrders.map((po) => po.id)), [linkedPurchaseOrders]);

  const candidateOrders: PurchaseOrder[] = useMemo(() => {
    const rows = purchaseOrdersQuery.data?.data ?? [];
    return rows
      .filter((po) => po.status !== 'cancelled')
      .filter((po) => po.status !== 'draft')
      .filter((po) => !linkedPoIds.has(po.id));
  }, [purchaseOrdersQuery.data?.data, linkedPoIds]);

  const saveChanges = () => {
    if (!shipmentId) return;
    updateShipment.mutate({
      shipmentId,
      freightForwarderId: form.freightForwarderId ? form.freightForwarderId : null,
      shippedDate: form.shippedDate.trim() ? form.shippedDate.trim() : null,
      expectedDeliveryDate: form.expectedDeliveryDate.trim() ? form.expectedDeliveryDate.trim() : null,
      // Cast-gated new logistics fields (migration 20260706060000).
      intoPortDate: form.intoPortDate.trim() ? form.intoPortDate.trim() : null,
      atWarehouseDate: form.atWarehouseDate.trim() ? form.atWarehouseDate.trim() : null,
      modeOfTransport: form.modeOfTransport ? form.modeOfTransport : null,
      totalUnits: form.totalUnits.trim() ? Number(form.totalUnits) : null,
      totalCartons: form.totalCartons.trim() ? Number(form.totalCartons) : null,
      invoiceNumber: form.invoiceNumber.trim() ? form.invoiceNumber.trim() : null,
      invoiceDate: form.invoiceDate.trim() ? form.invoiceDate.trim() : null,
      weight: form.weight.trim() ? Number(form.weight) : null,
      cbm: form.cbm.trim() ? Number(form.cbm) : null,
      trackingLink: form.trackingLink.trim() ? form.trackingLink.trim() : null,
      freightCost: form.freightCost.trim() ? Number(form.freightCost) : null,
      currency: normalizeCurrency(form.currency || orgCurrency),
      exchangeRate: form.exchangeRate.trim() ? Number(form.exchangeRate) : 1,
      taxRateId: form.taxRateId.trim() ? form.taxRateId.trim() : null,
      accountCode: form.accountCode.trim() ? form.accountCode.trim() : null,
      notes: form.notes.trim() ? form.notes.trim() : null,
    } as Parameters<typeof updateShipment.mutate>[0]);
  };

  const previewFreight = () => {
    if (!form.freightCost.trim()) return '-';
    const value = Number(form.freightCost);
    if (!Number.isFinite(value)) return '-';
    return formatMoney(value, form.currency || null);
  };

  const destinationName = shipmentLocationId ? (locationLookup.get(shipmentLocationId) ?? '-') : '-';

  // Push shipment identity into the centred TopHeader slot (back → /shipments).
  const headerTitle = useMemo(
    () => (shipment ? (shipment.shipment_number ?? 'Shipment') : null),
    [shipment],
  );
  const headerSubtitle = useMemo(() => {
    if (!shipment) return null;
    const forwarder = shipment.freight_forwarder?.name;
    return forwarder ? `${forwarder} · ${destinationName}` : destinationName;
  }, [shipment, destinationName]);
  usePageTitle(headerTitle, headerSubtitle, { label: 'Back to Shipments', to: '/shipments' });

  return (
    <RoleGuard requirePurchaseOrderAccess>
      <div className="flex flex-col">
        <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8 space-y-6">
          {/* "Back to Shipments" lives in the TopHeader (typewriter button). */}
          {shipmentQuery.isLoading ? (
            <PageLoader pool="shipment" />
          ) : shipmentQuery.error ? (
            <div className="rounded-md border p-8 text-center text-destructive">{shipmentQuery.error.message}</div>
          ) : !shipment ? (
            <div className="rounded-md border p-8 text-center text-muted-foreground">Shipment not found.</div>
          ) : (
            <>
              <PageActionBar
                ariaLabel="Shipment actions"
                left={
                  <Select
                    value={(shipment.status ?? 'pending') as ShipmentStatus}
                    onValueChange={(value) => handleStatusChange(value as ShipmentStatus)}
                    disabled={updateStatus.isPending}
                  >
                    <SelectTrigger
                      className={cn(
                        'w-[145px] h-8 border-0 text-xs font-medium',
                        shipmentStatusClassName((shipment.status ?? 'pending') as ShipmentStatus)
                      )}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value} className="text-xs">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                }
                right={
                  <>
                    {!isEditing ? (
                      <Button
                        variant="outline"
                        onClick={() => setIsEditing(true)}
                        className="h-8 px-3 text-xs gap-1.5"
                      >
                        <Edit className="h-3.5 w-3.5" />
                        Edit
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => setIsEditing(false)}
                          className="h-8 px-3 text-xs gap-1.5"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={saveChanges}
                          disabled={updateShipment.isPending}
                          className="h-8 px-3 text-xs gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                        >
                          Save
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline"
                      className="h-8 px-3 text-xs gap-1.5 border-threadheads-purple/40 text-threadheads-purple hover:bg-threadheads-purple/10 hover:text-threadheads-purple"
                      onClick={() => setNotifyOpen(true)}
                      disabled={notifyWarehouse.isPending}
                    >
                      {notifyWarehouse.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Send className="h-3.5 w-3.5" />
                      )}
                      Notify warehouse
                    </Button>
                    <PageActionBarDivider />
                    <Button
                      variant="destructive"
                      onClick={() => setConfirmDeleteOpen(true)}
                      className="h-8 px-3 text-xs gap-1.5"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </Button>
                  </>
                }
              />

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
                {/* Left column */}
                <div className="space-y-6 min-w-0">
                  {/* Shipment Details */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Shipment Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Logistics dates — the repo Calendar+Popover picker */}
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <FormDateField
                          label="Ship Date"
                          isEditing={isEditing}
                          value={form.shippedDate}
                          onChange={(v) => setForm((prev) => ({ ...prev, shippedDate: v }))}
                        />
                        <FormDateField
                          label="ETA"
                          isEditing={isEditing}
                          value={form.expectedDeliveryDate}
                          onChange={(v) => setForm((prev) => ({ ...prev, expectedDeliveryDate: v }))}
                        />
                        <FormDateField
                          label="Into port date"
                          isEditing={isEditing}
                          value={form.intoPortDate}
                          onChange={(v) => setForm((prev) => ({ ...prev, intoPortDate: v }))}
                        />
                        <FormDateField
                          label="At warehouse date"
                          isEditing={isEditing}
                          value={form.atWarehouseDate}
                          onChange={(v) => setForm((prev) => ({ ...prev, atWarehouseDate: v }))}
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Mode of transport</Label>
                          {isEditing ? (
                            <Select
                              value={form.modeOfTransport || '__none__'}
                              onValueChange={(value) =>
                                setForm((prev) => ({
                                  ...prev,
                                  modeOfTransport:
                                    value === '__none__'
                                      ? ''
                                      : (value as 'sea_freight' | 'air_freight' | 'courier'),
                                }))
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select transport" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">Not set</SelectItem>
                                {TRANSPORT_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="text-sm">{transportLabel(shipmentExtras?.mode_of_transport) ?? '-'}</div>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Freight Forwarder</Label>
                          {isEditing ? (
                            <Select
                              value={form.freightForwarderId || '__none__'}
                              onValueChange={(value) => setForm((prev) => ({ ...prev, freightForwarderId: value === '__none__' ? '' : value }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select forwarder" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">No forwarder</SelectItem>
                                {forwarders.map((f) => (
                                  <SelectItem key={f.id} value={f.id}>
                                    {f.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="text-sm">{shipment.freight_forwarder?.name ?? '-'}</div>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Total units</Label>
                          {isEditing ? (
                            <Input
                              inputMode="numeric"
                              type="number"
                              min={0}
                              value={form.totalUnits}
                              onChange={(e) => setForm((prev) => ({ ...prev, totalUnits: e.target.value }))}
                              placeholder="0"
                              className="tabular-nums"
                            />
                          ) : (
                            <div className="text-sm tabular-nums">{shipmentExtras?.total_units ?? '-'}</div>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Total cartons</Label>
                          {isEditing ? (
                            <Input
                              inputMode="numeric"
                              type="number"
                              min={0}
                              value={form.totalCartons}
                              onChange={(e) => setForm((prev) => ({ ...prev, totalCartons: e.target.value }))}
                              placeholder="0"
                              className="tabular-nums"
                            />
                          ) : (
                            <div className="text-sm tabular-nums">{shipmentExtras?.total_cartons ?? '-'}</div>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Tracking Link</Label>
                          {isEditing ? (
                            <Input
                              value={form.trackingLink}
                              onChange={(e) => setForm((prev) => ({ ...prev, trackingLink: e.target.value }))}
                              placeholder="URL or tracking number"
                            />
                          ) : (
                            <div className="text-sm">
                              {shipment.tracking_link ? (
                                <a
                                  href={shipment.tracking_link}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-primary hover:underline break-all"
                                >
                                  {shipment.tracking_link}
                                </a>
                              ) : (
                                '-'
                              )}
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Weight (kg)</Label>
                            {isEditing ? (
                              <Input
                                inputMode="decimal"
                                value={form.weight}
                                onChange={(e) => setForm((prev) => ({ ...prev, weight: e.target.value }))}
                              />
                            ) : (
                              <div className="text-sm">{shipment.weight ?? '-'}</div>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label>CBM</Label>
                            {isEditing ? (
                              <Input
                                inputMode="decimal"
                                value={form.cbm}
                                onChange={(e) => setForm((prev) => ({ ...prev, cbm: e.target.value }))}
                              />
                            ) : (
                              <div className="text-sm">{shipment.cbm ?? '-'}</div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Notes</Label>
                        {isEditing ? (
                          <Textarea
                            value={form.notes}
                            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                            placeholder="Optional notes..."
                          />
                        ) : (
                          <div className="text-sm whitespace-pre-wrap">{shipment.notes ?? '-'}</div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Documents */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Paperclip className="h-4 w-4 mr-2" />
                        Documents
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-5">
                      <DocumentSlot
                        label="Arrival notice"
                        fileType="arrival_notice"
                        files={filesByType.arrival_notice}
                        uploading={uploadingFileType === 'arrival_notice'}
                        onUpload={(file) => void handleUploadShipmentFile('arrival_notice', file)}
                        onDownload={handleDownloadShipmentFile}
                        onDelete={handleDeleteShipmentFile}
                      />
                      <Separator />
                      <DocumentSlot
                        label="Packing list"
                        fileType="packing_list"
                        files={filesByType.packing_list}
                        uploading={uploadingFileType === 'packing_list'}
                        onUpload={(file) => void handleUploadShipmentFile('packing_list', file)}
                        onDownload={handleDownloadShipmentFile}
                        onDelete={handleDeleteShipmentFile}
                      />
                      <Separator />
                      <DocumentSlot
                        label="Invoices"
                        fileType="invoice"
                        files={filesByType.invoice}
                        uploading={uploadingFileType === 'invoice'}
                        onUpload={(file) => void handleUploadShipmentFile('invoice', file)}
                        onDownload={handleDownloadShipmentFile}
                        onDelete={handleDeleteShipmentFile}
                      />
                    </CardContent>
                  </Card>

                  {/* Costs */}
                  <ShipmentCostsCard
                    shipmentId={shipmentId}
                    orgCurrency={orgCurrency}
                    formatMoney={formatMoney}
                  />

                  {/* Linked Purchase Orders */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Linked Purchase Orders</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-muted-foreground">
                          {(linkedPurchaseOrders ?? []).length} linked
                        </div>
                        <Popover open={poPickerOpen} onOpenChange={setPoPickerOpen}>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Plus className="mr-2 h-4 w-4" />
                              Link PO
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[420px] p-0" align="end">
                            <Command>
                              <CommandInput placeholder="Search purchase orders..." />
                              <CommandList>
                                <CommandEmpty>
                                  {purchaseOrdersQuery.isLoading ? 'Loading...' : 'No purchase orders found.'}
                                </CommandEmpty>
                                <CommandGroup>
                                  {candidateOrders.map((po) => (
                                    <CommandItem
                                      key={po.id}
                                      onSelect={() => linkPo.mutate({ shipmentId, purchaseOrderId: po.id })}
                                      className="flex items-center justify-between"
                                    >
                                      <span className="font-medium">
                                        {po.po_number ?? '-'}{' '}
                                        <span className="text-muted-foreground font-normal">
                                          {po.title ? `· ${po.title}` : ''}
                                        </span>
                                      </span>
                                      <Badge variant="muted">{(po.status ?? '').replace('_', ' ')}</Badge>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>

                      {(linkedPurchaseOrders ?? []).length === 0 ? (
                        <div className="rounded-md border p-4 text-sm text-muted-foreground">
                          No purchase orders linked yet.
                        </div>
                      ) : (
                        <div className="rounded-md border divide-y">
                          {linkedPurchaseOrders.map((po) => (
                            <div key={po.id} className="flex items-center justify-between p-3">
                              <div className="min-w-0">
                                <Link to={`/orders/${po.id}`} className="text-sm font-medium text-primary hover:underline">
                                  {po.po_number ?? '-'}
                                </Link>
                                <div className="text-xs text-muted-foreground truncate">
                                  {po.title ?? '-'} {po.supplier_name ? `· ${po.supplier_name}` : ''}{' '}
                                  {po.total_cost != null ? `· ${formatMoney(po.total_cost, po.currency ?? null)}` : ''}
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => unlinkPo.mutate({ shipmentId, purchaseOrderId: po.id })}
                                disabled={unlinkPo.isPending}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Right sidebar */}
                <div className="space-y-6">
                  {/* Details */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
                        <div className="space-y-1.5">
                          <div className="text-xs text-muted-foreground">Invoice Number</div>
                          {isEditing ? (
                            <Input
                              value={form.invoiceNumber}
                              onChange={(e) => setForm((prev) => ({ ...prev, invoiceNumber: e.target.value }))}
                              placeholder="Supplier invoice ref"
                            />
                          ) : (
                            <div className="text-sm">{shipment.invoice_number || '-'}</div>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <div className="text-xs text-muted-foreground">Invoice Date</div>
                          {isEditing ? (
                            <Input
                              type="date"
                              value={form.invoiceDate}
                              onChange={(e) => setForm((prev) => ({ ...prev, invoiceDate: e.target.value }))}
                            />
                          ) : (
                            <div className="text-sm">{formatDate(shipment.invoice_date ?? null)}</div>
                          )}
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-1.5">
                        <div className="text-xs text-muted-foreground">Currency</div>
                        {isEditing ? (
                          <Select value={form.currency} onValueChange={(value) => {
                            setForm((prev) => ({ ...prev, currency: value }));
                            if (normalizeCurrency(value) === orgCurrency) {
                              setForm((prev) => ({ ...prev, exchangeRate: '1' }));
                            }
                          }}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CURRENCY_OPTIONS.map((code) => (
                                <SelectItem key={code} value={code}>{code}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="text-sm">{shipment.currency ?? orgCurrency}</div>
                        )}
                      </div>

                      {normalizeCurrency(form.currency) !== orgCurrency && (
                        <div className="space-y-1.5">
                          <div className="text-xs text-muted-foreground">Exchange Rate</div>
                          {isEditing ? (
                            <Input
                              inputMode="decimal"
                              value={form.exchangeRate}
                              onChange={(e) => setForm((prev) => ({ ...prev, exchangeRate: e.target.value }))}
                            />
                          ) : (
                            <div className="text-sm">{shipment.exchange_rate ?? '-'}</div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Invoice & Billing */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Invoice & Billing</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-muted-foreground">Freight Cost</div>
                          {isEditing && form.freightCost.trim() && (
                            <div className="text-xs text-muted-foreground">{previewFreight()}</div>
                          )}
                        </div>
                        {isEditing ? (
                          <Input
                            inputMode="decimal"
                            value={form.freightCost}
                            onChange={(e) => setForm((prev) => ({ ...prev, freightCost: e.target.value }))}
                            placeholder="0.00"
                          />
                        ) : (
                          <div className="text-sm font-medium">
                            {shipment.freight_cost == null
                              ? '-'
                              : formatMoney(shipment.freight_cost, shipment.currency ?? null)}
                          </div>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <div className="text-xs text-muted-foreground">Tax Rate</div>
                        {isEditing ? (
                          <Select
                            value={form.taxRateId || '__none__'}
                            onValueChange={(value) => setForm((prev) => ({ ...prev, taxRateId: value === '__none__' ? '' : value }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select tax rate" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">No tax rate</SelectItem>
                              {(xeroTaxRatesQuery.data ?? []).map((taxRate) => (
                                <SelectItem key={taxRate.id} value={taxRate.id}>
                                  {taxRate.name} ({Math.round((taxRate.rate ?? 0) * 100)}%)
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="text-sm">
                            {(xeroTaxRatesQuery.data ?? []).find((taxRate) => taxRate.id === shipment.tax_rate_id)?.name || '-'}
                          </div>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <div className="text-xs text-muted-foreground">Account Code</div>
                        {isEditing ? (
                          <Select
                            value={form.accountCode || '__none__'}
                            onValueChange={(value) => setForm((prev) => ({ ...prev, accountCode: value === '__none__' ? '' : value }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select account code" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">No account code</SelectItem>
                              {(xeroAccountsQuery.data ?? []).map((account) => (
                                <SelectItem key={account.id} value={account.account_code}>
                                  {account.account_code} · {account.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="text-sm">{shipment.account_code || '-'}</div>
                        )}
                      </div>

                      <Separator />

                      <div className="space-y-1.5">
                        <div className="text-xs text-muted-foreground">Xero Bill</div>
                        <div className="text-sm">
                          {shipment.xero_bill_id ? (
                            <span className="text-emerald-600">{shipment.xero_bill_id}</span>
                          ) : (
                            'Not pushed'
                          )}
                        </div>
                      </div>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-block w-full">
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full"
                                onClick={() => pushShipmentToXero.mutate({ shipmentId })}
                                disabled={!shipment || pushShipmentToXero.isPending || !hasInvoice}
                              >
                                {pushShipmentToXero.isPending ? (
                                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                  <Link2 className="mr-2 h-4 w-4" />
                                )}
                                {shipment.xero_bill_id ? 'Update Xero Bill' : 'Push to Xero'}
                              </Button>
                            </span>
                          </TooltipTrigger>
                          {!hasInvoice && (
                            <TooltipContent>
                              Upload an invoice to push to Xero
                            </TooltipContent>
                          )}
                        </Tooltip>
                      </TooltipProvider>
                    </CardContent>
                  </Card>

                  {/* Summary */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Summary</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Location</span>
                        <span className="font-medium">{destinationName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Forwarder</span>
                        <span className="font-medium">{shipment.freight_forwarder?.name ?? '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Transport</span>
                        <span className="font-medium">{transportLabel(shipmentExtras?.mode_of_transport) ?? '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Cartons / Units</span>
                        <span className="font-medium tabular-nums">
                          {shipmentExtras?.total_cartons ?? '-'} / {shipmentExtras?.total_units ?? '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Linked POs</span>
                        <span className="font-medium">{linkedPurchaseOrders.length}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Freight</span>
                        <span className="font-medium">
                          {shipment.freight_cost == null
                            ? '-'
                            : formatMoney(shipment.freight_cost, shipment.currency ?? null)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
              <CommentTimeline entityType="shipment" entityId={shipmentId} />
            </>
          )}
          <BackToTopPill />
        </main>
      </div>

      {/* Notify warehouse confirmation */}
      <AlertDialog open={notifyOpen} onOpenChange={setNotifyOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Notify warehouse?</AlertDialogTitle>
            <AlertDialogDescription>
              This posts a heads-up to the warehouse Slack channel with the details below.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-md border bg-muted/40 p-3 space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Shipment</span>
              <span className="font-medium">{shipment?.shipment_number ?? '-'}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Destination</span>
              <span className="font-medium text-right">{destinationName}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Cartons / Units</span>
              <span className="font-medium tabular-nums">
                {shipmentExtras?.total_cartons ?? '-'} / {shipmentExtras?.total_units ?? '-'}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">ETA</span>
              <span className="font-medium">{formatDate(shipment?.expected_delivery_date ?? null)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Transport</span>
              <span className="font-medium">{transportLabel(shipmentExtras?.mode_of_transport) ?? '-'}</span>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (shipmentId) notifyWarehouse.mutate({ shipmentId });
              }}
              disabled={notifyWarehouse.isPending}
              className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
            >
              {notifyWarehouse.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Notify warehouse
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete shipment?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. The shipment will be removed and links to purchase orders will be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteShipment.mutate({ shipmentId })}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </RoleGuard>
  );
}

// ----------------------------------------------------------------------------
// FormDateField — label + Calendar/Popover picker (edit) / formatted text
// (read). Bound to a `YYYY-MM-DD` string so it drops straight into the form
// state and the updateShipment payload. Mirrors ProductDevDetail's DeadlineCard
// picker (the repo's canonical Calendar+Popover date affordance).
// ----------------------------------------------------------------------------

type ShipmentFileRow = {
  id: string;
  file_name: string;
  file_size: number | null;
  file_type: string;
};

function FormDateField({
  label,
  isEditing,
  value,
  onChange,
}: {
  label: string;
  isEditing: boolean;
  value: string;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;

  if (!isEditing) {
    return (
      <div className="space-y-1.5">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm">{formatDate(value || null)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-start font-normal">
            <CalendarDays className="mr-2 h-4 w-4 text-muted-foreground" />
            {selected ? format(selected, 'd MMM yyyy') : <span className="text-muted-foreground">Pick a date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(date) => {
              onChange(date ? format(date, 'yyyy-MM-dd') : '');
              setOpen(false);
            }}
            initialFocus
            className="pointer-events-auto"
          />
          {value && (
            <div className="border-t p-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-full px-2 text-xs text-muted-foreground hover:text-destructive"
                onClick={() => {
                  onChange('');
                  setOpen(false);
                }}
              >
                <X className="mr-1.5 h-3.5 w-3.5" /> Clear
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ----------------------------------------------------------------------------
// DocumentSlot — labelled upload slot with drag/drop + click, plus a list of
// uploaded files with download/delete. Mirrors OrderDetail's per-type Files
// sections; adds a drop target.
// ----------------------------------------------------------------------------

function DocumentSlot({
  label,
  fileType,
  files,
  uploading,
  onUpload,
  onDownload,
  onDelete,
}: {
  label: string;
  fileType: ShipmentFileType;
  files: ShipmentFileRow[];
  uploading: boolean;
  onUpload: (file: globalThis.File) => void;
  onDownload: (fileId: string) => void;
  onDelete: (fileId: string, fileName: string) => void;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const inputId = `shipment-doc-${fileType}`;

  const handleFiles = (list: FileList | null) => {
    if (!list) return;
    Array.from(list).forEach((file) => onUpload(file));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label htmlFor={inputId} className="text-sm text-muted-foreground">{label}</Label>
        <label htmlFor={inputId}>
          <input
            id={inputId}
            type="file"
            multiple
            className="hidden"
            accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.webp"
            onChange={(event) => {
              handleFiles(event.target.files);
              event.target.value = '';
            }}
            disabled={uploading}
          />
          <Button variant="ghost" size="sm" className="h-7 px-2" asChild>
            <span>
              {uploading ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
            </span>
          </Button>
        </label>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          'rounded-md border border-dashed transition-colors',
          isDragging
            ? 'border-threadheads-purple bg-threadheads-purple/5'
            : 'border-border',
        )}
      >
        {files.length === 0 ? (
          <label
            htmlFor={inputId}
            className="flex cursor-pointer items-center justify-center gap-2 px-3 py-4 text-sm text-muted-foreground"
          >
            <FileText className="h-4 w-4" />
            Drop a file or click to upload
          </label>
        ) : (
          <div className="space-y-1 p-1.5">
            {files.map((row) => (
              <div key={row.id} className="flex items-center gap-2 text-sm bg-muted rounded px-2 py-1.5">
                <File className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="truncate flex-1" title={row.file_name}>
                  {row.file_name}
                </span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatBytes(row.file_size)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 shrink-0"
                  onClick={() => onDownload(row.id)}
                  aria-label={`Download ${row.file_name}`}
                >
                  <Download className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 shrink-0 text-red-500 hover:text-red-600"
                  onClick={() => onDelete(row.id, row.file_name)}
                  aria-label={`Delete ${row.file_name}`}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// ShipmentCostsCard — cost-lines editor. Each row is [type][amount][currency]
// [notes][remove]. Save resends the WHOLE array (with ids for existing rows)
// via setShipmentCosts — a full-replace reconcile, clobber-safe like the trims
// editor. Per-currency subtotals (grouped, never summed across currencies).
//
// Cast-gated: shipments.listShipmentCosts / setShipmentCosts land with the
// parallel backend PR (migration 20260706060000).
// ----------------------------------------------------------------------------

type CostDraftRow = {
  key: string;
  id?: string;
  cost_type: ShipmentCostType;
  amount: string;
  currency: string;
  notes: string;
};

const makeCostKey = () =>
  (typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `row-${Math.random().toString(36).slice(2)}`);

const toDraftRow = (row: ShipmentCostRow): CostDraftRow => ({
  key: row.id,
  id: row.id,
  cost_type: row.cost_type,
  amount: String(row.amount),
  currency: row.currency,
  notes: row.notes ?? '',
});

function ShipmentCostsCard({
  shipmentId,
  orgCurrency,
  formatMoney,
}: {
  shipmentId: string;
  orgCurrency: string;
  formatMoney: (value: number, currencyOverride?: string | null) => string;
}) {
  const shipmentsExt = trpc.shipments as unknown as ShipmentsExtApi;
  const utils = trpc.useUtils();
  const costsExtUtils = utils.shipments as unknown as {
    listShipmentCosts: { invalidate: (input: { shipmentId: string }) => Promise<void> };
  };

  const costsQuery = shipmentsExt.listShipmentCosts.useQuery(
    { shipmentId },
    { enabled: Boolean(shipmentId), staleTime: 30_000 },
  );

  const setCosts = shipmentsExt.setShipmentCosts.useMutation({
    onSuccess: async () => {
      await costsExtUtils.listShipmentCosts.invalidate({ shipmentId });
      await costsQuery.refetch();
      toast({ title: 'Costs saved' });
    },
    onError: (err) =>
      toast({ title: 'Failed to save costs', description: err.message, variant: 'destructive' }),
  });

  const serverRows = useMemo(() => costsQuery.data ?? [], [costsQuery.data]);
  const [draft, setDraft] = useState<CostDraftRow[]>([]);
  const [dirty, setDirty] = useState(false);

  // Reseed from the server whenever fresh data arrives and the user has no
  // pending edits — never clobber an in-progress edit.
  useEffect(() => {
    if (dirty) return;
    setDraft(serverRows.map(toDraftRow));
  }, [serverRows, dirty]);

  const patchRow = (key: string, patch: Partial<CostDraftRow>) => {
    setDraft((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const removeRow = (key: string) => {
    setDraft((prev) => prev.filter((r) => r.key !== key));
    setDirty(true);
  };

  const addRow = () => {
    setDraft((prev) => [
      ...prev,
      { key: makeCostKey(), cost_type: 'freight', amount: '', currency: orgCurrency, notes: '' },
    ]);
    setDirty(true);
  };

  const resetDraft = () => {
    setDraft(serverRows.map(toDraftRow));
    setDirty(false);
  };

  const parsedAmount = (raw: string) => {
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };
  const hasInvalidRow = draft.some((r) => r.amount.trim() !== '' && parsedAmount(r.amount) === null);
  const hasEmptyAmount = draft.some((r) => r.amount.trim() === '');

  // Per-currency subtotals — grouped, never summed across currencies.
  const subtotals = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of draft) {
      const amt = parsedAmount(r.amount);
      if (amt === null) continue;
      const code = normalizeCurrency(r.currency);
      map.set(code, (map.get(code) ?? 0) + amt);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [draft]);

  const handleSave = () => {
    const payload: ShipmentCostInput[] = draft
      .filter((r) => r.amount.trim() !== '' && parsedAmount(r.amount) !== null)
      .map((r) => ({
        id: r.id,
        costType: r.cost_type,
        amount: parsedAmount(r.amount) as number,
        currency: normalizeCurrency(r.currency),
        notes: r.notes.trim() ? r.notes.trim() : null,
      }));
    setCosts.mutate({ shipmentId, costs: payload });
    setDirty(false);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2">
          <Boxes className="h-4 w-4" />
          Costs
          {draft.length > 0 && (
            <span className="text-[11px] text-muted-foreground tabular-nums">{draft.length}</span>
          )}
        </CardTitle>
        <Button variant="outline" size="sm" onClick={addRow}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add cost
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {costsQuery.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : costsQuery.error ? (
          <p className="text-sm text-destructive">{costsQuery.error.message}</p>
        ) : draft.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No cost lines yet. Add duties, freight, or other charges (each with its own currency).
          </p>
        ) : (
          <div className="space-y-2">
            {draft.map((row) => {
              const invalid = row.amount.trim() !== '' && parsedAmount(row.amount) === null;
              return (
                <div
                  key={row.key}
                  className="flex flex-wrap items-start gap-2 sm:flex-nowrap"
                >
                  <Select
                    value={row.cost_type}
                    onValueChange={(value) => patchRow(row.key, { cost_type: value as ShipmentCostType })}
                  >
                    <SelectTrigger className="h-9 w-[120px] shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COST_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="w-[110px] shrink-0">
                    <Input
                      inputMode="decimal"
                      value={row.amount}
                      onChange={(e) => patchRow(row.key, { amount: e.target.value })}
                      placeholder="0.00"
                      aria-label="Amount"
                      className={cn('h-9 tabular-nums text-right', invalid && 'border-destructive')}
                    />
                  </div>
                  <Select
                    value={normalizeCurrency(row.currency)}
                    onValueChange={(value) => patchRow(row.key, { currency: value })}
                  >
                    <SelectTrigger className="h-9 w-[90px] shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map((code) => (
                        <SelectItem key={code} value={code}>{code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={row.notes}
                    onChange={(e) => patchRow(row.key, { notes: e.target.value })}
                    placeholder="Notes (optional)"
                    aria-label="Notes"
                    className="h-9 flex-1 min-w-[120px]"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 shrink-0 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeRow(row.key)}
                    aria-label="Remove cost line"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {subtotals.length > 0 && (
          <div className="rounded-md border bg-muted/30 p-3 space-y-1">
            <div className="text-xs font-medium text-muted-foreground">Subtotals by currency</div>
            {subtotals.map(([code, total]) => (
              <div key={code} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{code}</span>
                <span className="font-medium tabular-nums">{formatMoney(total, code)}</span>
              </div>
            ))}
          </div>
        )}

        {hasInvalidRow && (
          <p className="text-sm text-destructive">Enter a valid amount (0 or more) for each cost line.</p>
        )}

        {dirty && (
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={resetDraft} disabled={setCosts.isPending}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
              onClick={handleSave}
              disabled={setCosts.isPending || hasInvalidRow || hasEmptyAmount}
            >
              {setCosts.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : null}
              Save costs
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
